import { useState } from "react";
import { 
  SparklesIcon,
  PlusIcon,
  UsersIcon,
  CurrencyDollarIcon
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { toast } from "react-hot-toast";
import type { ProjectResource } from "@/features/Projects/models/project.types";
import type { ResourcesPhaseProps } from "./resourcesPhase.types";
import { ResourceEditor, ResourcesList, ResourcesSummary, EmptyResourcesState } from "./components";
import { generateResourceId, calculateResourceCost } from "./resourcesPhase.definitions";

export default function ResourcesPhase({ project, onSave, onNext }: ResourcesPhaseProps) {
  const [editingResource, setEditingResource] = useState<ProjectResource | null | undefined>(undefined);
  const [resources, setResources] = useState<ProjectResource[]>(project.resources || []);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const estimatedPrice = project.estimatedPrice || resources.reduce((sum, resource) => sum + resource.totalCost, 0);

  const handleGenerateWithAI = async () => {
    setIsGenerating(true);
    try {
      // Implementar geração real com IA via ResourcesService
      const { ResourcesService } = await import("../../../../services/ResourcesService");
      const resourcesService = new ResourcesService();
      
      const request = {
        projectDescription: project.description,
        objectives: project.objectives || [],
        technicalSolution: project.technicalSolution,
        timeline: project.timeline,
        stories: project.stories,
        schema: project.schema,
        includeProjectManager: true,
        includeDesigner: true,
        includeDevOps: true,
        includeQA: true,
      };

      const response = await resourcesService.generateResources(request);
      
      // Processar recursos retornados pela IA
      const aiResources: ProjectResource[] = response.resources.map((resource: any) => ({
        ...resource,
        totalCost: calculateResourceCost(resource.hoursEstimated, resource.hourlyRate),
      }));

      setResources(aiResources);
      toast.success(`${aiResources.length} recursos gerados com sucesso!`);
      
    } catch (error) {
      console.error("Erro ao gerar recursos:", error);
      toast.error("Erro ao gerar recursos com IA");
      
      // Fallback com recursos básicos em caso de erro
      const fallbackResources: ProjectResource[] = [
        {
          id: generateResourceId(),
          name: "Desenvolvedor Full-stack Sênior",
          type: "developer",
          hoursEstimated: 120,
          hourlyRate: 112,
          totalCost: calculateResourceCost(120, 112),
        },
        {
          id: generateResourceId(),
          name: "Designer UI/UX Pleno", 
          type: "designer",
          hoursEstimated: 40,
          hourlyRate: 60,
          totalCost: calculateResourceCost(40, 60),
        }
      ];
      
      setResources(fallbackResources);
      toast.success("Recursos básicos criados (IA indisponível)");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddResource = () => {
    setEditingResource(null); // null = novo recurso
  };

  const handleEditResource = (resource: ProjectResource) => {
    setEditingResource(resource);
  };

  const handleSaveResource = (resource: ProjectResource) => {
    const updatedResources = editingResource === null 
      ? [...resources, resource] // Novo recurso
      : resources.map(r => r.id === resource.id ? resource : r); // Editar existente

    setResources(updatedResources);
    setEditingResource(undefined);
    
    const action = editingResource === null ? "criado" : "atualizado";
    toast.success(`Recurso ${action} com sucesso!`);
  };

  const handleCancelEdit = () => {
    setEditingResource(undefined);
  };

  const handleDeleteResource = (resourceId: string) => {
    const updatedResources = resources.filter(r => r.id !== resourceId);
    setResources(updatedResources);
    setShowDeleteConfirm(null);
    toast.success("Recurso removido com sucesso!");
  };

  const handleSaveAll = async () => {
    const totalCost = resources.reduce((sum, resource) => sum + resource.totalCost, 0);
    await onSave(resources, totalCost);
    toast.success("Recursos salvos com sucesso!");
  };

  const handleFinishPhase = () => {
    onNext();
  };

  const hasResources = resources.length > 0;

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          onClick={handleGenerateWithAI}
          disabled={isGenerating}
          leftIcon={<SparklesIcon className="h-4 w-4" />}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isGenerating ? "Gerando..." : "Gerar com IA"}
        </Button>

        <Button
          onClick={handleAddResource}
          variant="outline"
          leftIcon={<PlusIcon className="h-4 w-4" />}
        >
          Novo Recurso
        </Button>

        {hasResources && (
          <Button
            onClick={handleSaveAll}
            variant="outline"
            leftIcon={<CurrencyDollarIcon className="h-4 w-4" />}
          >
            Salvar Recursos
          </Button>
        )}
      </div>

      {/* Generation Status */}
      {isGenerating && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <div>
              <p className="text-sm font-medium text-blue-800">
                Analisando projeto e gerando equipe necessária...
              </p>
              <p className="text-xs text-blue-600">
                Considerando complexidade técnica, timeline e user stories.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Resource Editor */}
      {editingResource !== undefined && (
        <ResourceEditor
          resource={editingResource}
          onSave={handleSaveResource}
          onCancel={handleCancelEdit}
        />
      )}

      {!hasResources && editingResource === undefined ? (
        // Empty State
        <EmptyResourcesState 
          onGenerateWithAI={handleGenerateWithAI}
          onAddResource={handleAddResource}
          isGenerating={isGenerating}
        />
      ) : (
        // Resources Content
        <div className="space-y-6">
          {/* Resources Summary */}
          {hasResources && (
            <ResourcesSummary 
              resources={resources}
              estimatedPrice={estimatedPrice}
            />
          )}

          {/* Resources List */}
          {hasResources && (
            <ResourcesList
              resources={resources}
              onEdit={handleEditResource}
              onDelete={handleDeleteResource}
              showDeleteConfirm={showDeleteConfirm}
              setShowDeleteConfirm={setShowDeleteConfirm}
            />
          )}

          {/* Action Buttons */}
          {hasResources && (
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {resources.length} recurso{resources.length !== 1 ? 's' : ''} • 
                Estimativa total: <span className="font-semibold text-gray-900">
                  R$ {estimatedPrice.toLocaleString('pt-BR')}
                </span>
              </div>
              
              <Button
                onClick={handleFinishPhase}
                leftIcon={<UsersIcon className="h-4 w-4" />}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                Avançar para Proposta
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}